import * as React from 'react'

// The sync runs in the background, poll until the last sync date changes
const POLL_INTERVAL = 5000
const MAX_POLL_ATTEMPTS = 24

interface AccountingSyncStatusLike {
  last_accounting_sync_at: string | null
}

interface Options<T extends AccountingSyncStatusLike> {
  getStatus: () => Promise<T>
  // Resolves to true when the sync was started
  synchronize: () => Promise<boolean>
  onCompleted?: () => void
  // Reloads the status when it changes (e.g. the selected workspace)
  key?: string
}

export function useAccountingSync<T extends AccountingSyncStatusLike>({ getStatus, synchronize, onCompleted, key }: Options<T>) {
  const [status, setStatus] = React.useState<T | null>(null)
  const [syncing, setSyncing] = React.useState(false)
  const pollTimeout = React.useRef<number>()
  // Bumped on every key change and on unmount so stale responses are ignored
  const generation = React.useRef(0)

  React.useEffect(() => {
    const requestGeneration = ++generation.current
    setStatus(null)
    setSyncing(false)

    getStatus()
      .then(response => { if (generation.current === requestGeneration) setStatus(response) })
      .catch(console.error)

    return () => {
      generation.current++
      window.clearTimeout(pollTimeout.current)
    }
  }, [key])

  const pollUntilSynced = (previousSyncAt: string | null, requestGeneration: number, attempt = 0) => {
    pollTimeout.current = window.setTimeout(async () => {
      try {
        const response = await getStatus()
        if (generation.current !== requestGeneration) return

        if (response.last_accounting_sync_at !== previousSyncAt) {
          setStatus(response)
          setSyncing(false)
          onCompleted?.()
          return
        }
      } catch (ex) {
        console.error(ex)
      }

      if (attempt + 1 < MAX_POLL_ATTEMPTS) {
        pollUntilSynced(previousSyncAt, requestGeneration, attempt + 1)
      } else {
        setSyncing(false)
      }
    }, POLL_INTERVAL)
  }

  const sync = async (): Promise<boolean> => {
    const requestGeneration = generation.current
    setSyncing(true)

    try {
      const started = await synchronize()
      if (generation.current !== requestGeneration) return false

      if (started) {
        pollUntilSynced(status?.last_accounting_sync_at ?? null, requestGeneration)
      } else {
        setSyncing(false)
      }

      return started
    } catch (ex) {
      console.error(ex)
      if (generation.current === requestGeneration) setSyncing(false)
      return false
    }
  }

  return { status, syncing, sync }
}
